import { Toast } from "@skbkontur/react-ui";
import isEqual from "lodash.isequal";
import { GetDocumentsQuery } from "@skbkontur/staff-common/lib/client/clientTypes";

import { CandyThunkAction } from "../../../Engine/Core/Store/CandyReduxTypes";
import { Deferred } from "../../../Helpers/Deferred";
import {
    EmployerDocument,
    EmployerNormalizedTag,
    ImportEmployerDocumentsFilter,
    ImportEmployerDocumentsFilterType,
    ImportEmployerDocumentsOptions,
    RequestsTags,
} from "../../EmployeeServiceTypes";
import { getCandyHub } from "../../../Forms/Sentry/InitializeSentry";

import { EmployerDocumentsActionType } from "./EmployerDocumentsAction";
import { getFilterSelectValue, getSelectedFilterValueByType } from "./EmployerDocumentsStateUtils";

export const importDocuments =
    (
        deferredResult: Deferred<EmployerNormalizedTag | undefined>,
        options: ImportEmployerDocumentsOptions
    ): CandyThunkAction =>
    async dispatch => {
        const filterType = options.filter
            ? (options.filter.defaultValue?.filterType ?? ImportEmployerDocumentsFilterType.Month)
            : undefined;
        const filterValue =
            options.filter && filterType
                ? (options.filter.defaultValue?.selectValue ??
                  getSelectedFilterValueByType(
                      filterType,
                      options.filter?.selectValues ?? ({} as ImportEmployerDocumentsFilter["selectValues"])
                  ))
                : undefined;

        dispatch({
            type: EmployerDocumentsActionType.BeginImport,
            payload: {
                deferredResult: deferredResult,
                options: options,
                filterType: filterType,
                filterValue: filterValue,
            },
        });

        let requestsTags = options.requestsTags;
        if (options.filter != undefined && filterType != undefined) {
            requestsTags =
                getFilterSelectValue(filterValue, options.filter?.selectValues[filterType] ?? [])?.requestsTags ?? [];
        }

        dispatch(updateDocuments(requestsTags ?? [], options));
    };

export const changeFilterType =
    (filterType: ImportEmployerDocumentsFilterType): CandyThunkAction =>
    async (dispatch, getState) => {
        dispatch({
            type: EmployerDocumentsActionType.ChangeFilterType,
            payload: {
                filterType: filterType,
            },
        });

        const employerDocuments = getState().employerDocuments;
        const requestsTags =
            getFilterSelectValue(
                employerDocuments?.filterValue,
                employerDocuments?.options?.filter?.selectValues[filterType] ?? []
            )?.requestsTags ?? [];

        dispatch(updateDocuments(requestsTags, employerDocuments?.options));
    };

export const changeFilterValue =
    (filterValue: string | number): CandyThunkAction =>
    async (dispatch, getState) => {
        const employerDocuments = getState().employerDocuments;
        const filterType = employerDocuments?.filterType as ImportEmployerDocumentsFilterType;
        const requestsTags =
            getFilterSelectValue(filterValue, employerDocuments?.options?.filter?.selectValues[filterType] ?? [])
                ?.requestsTags ?? [];

        const oldFilterValue = employerDocuments?.filterValue;
        const oldRequestsTags =
            getFilterSelectValue(oldFilterValue, employerDocuments?.options?.filter?.selectValues[filterType] ?? [])
                ?.requestsTags ?? [];

        dispatch({
            type: EmployerDocumentsActionType.ChangeFilterValue,
            payload: {
                filterValue: filterValue,
            },
        });

        if (isEqual(requestsTags, oldRequestsTags)) {
            return;
        }

        dispatch(updateDocuments(requestsTags, employerDocuments?.options));
    };

export const updateDocuments =
    (requestsTags: RequestsTags, options?: ImportEmployerDocumentsOptions): CandyThunkAction =>
    async (dispatch, getState, form) => {
        try {
            dispatch({
                type: EmployerDocumentsActionType.BeginLoading,
            });

            const fields: GetDocumentsQuery["fields"] = ["id", "tags", "employeeIds"];
            if (options?.filterByEmployeeRelations) {
                fields.push("employeeRelations");
            }

            let existingEmployeeIds = options?.employeeIds || [];
            if (options?.employeeIds && options?.employeeIds?.length >= 0) {
                existingEmployeeIds = options?.employeeIds?.filter(Boolean) ?? [];
                if (existingEmployeeIds.length === 0) {
                    dispatch({
                        type: EmployerDocumentsActionType.CompleteLoading,
                        payload: {
                            documents: [],
                        },
                    });
                    return;
                }
            }

            const documents: EmployerDocument[] = [];
            const existingDocumentIds: Record<string, boolean> = {};

            for (const tags of requestsTags) {
                const documentsPart = await form.staffClient.getDocuments({
                    fields: fields,
                    tags: tags,
                    employeeIds: existingEmployeeIds,
                    ...options?.getDocumentsQuery,
                });
                for (const document of documentsPart.items) {
                    if (!existingDocumentIds[document.id]) {
                        existingDocumentIds[document.id] = true;
                        documents.push(document);
                    }
                }
            }
            dispatch({
                type: EmployerDocumentsActionType.CompleteLoading,
                payload: {
                    documents: documents,
                },
            });
        } catch (e) {
            Toast.push("Ошибка при получении списка документов");
            // eslint-disable-next-line no-console
            console.error(e);
            getState().employerDocuments?.resultDeferred?.resolve(undefined);
            dispatch({
                type: EmployerDocumentsActionType.CompleteLoading,
                payload: {
                    documents: [],
                },
            });
            getCandyHub().captureException(e);
        }
    };

export function submitImportDocument(tag: EmployerNormalizedTag): CandyThunkAction {
    return (dispatch, getState) => {
        getState().employerDocuments?.resultDeferred?.resolve(tag);
        dispatch({ type: EmployerDocumentsActionType.EndImport });
    };
}

export const cancelImportDocuments = (): CandyThunkAction => (dispatch, getState) => {
    getState().employerDocuments?.resultDeferred?.resolve(undefined);
    dispatch({ type: EmployerDocumentsActionType.EndImport });
};
